import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { EntityTypeId, LibraryItem, BundleType } from 'library-explorer';
import { GlossaryItem } from 'src/app/model/glossary-item';
import { LibraryResponseDto } from 'src/app/model/library-response-dto.model';
import { DownloadService } from 'src/app/services/api/download.service';
import { DocumentPreviewService } from 'src/app/shared/components/document-preview/services/document-preview.service';
import { UserActivityService } from '../user-activity.service';
import { FieldName, MediaPresignApiService, ProviderType } from 'library-explorer';
import { HttpService, DateRangeFilterService, DateRangeFilterOption } from 'library-explorer';
import { DataResponse } from '@app/model/data-response.model';
import { LibraryQuickListFilters } from '@app/components/library-page/models/library-quick-list-filters.enum';
import { LibraryAccessedInfo } from '@app/model/library-accessed-info';
import { ViewerTypeExtensions } from '@app/shared/components/document-preview/document-viewer-extensions';
import { FilePreviewType } from 'library-explorer';
import { LoadingToastService } from '../loading-toast.service';

@Injectable({
  providedIn: 'root'
})
export class LibraryService {
  private static readonly MAX_BUFFERED_DOWNLOAD_SIZE_BYTES = 50 * 1024 * 1024;

  private glossaries: Observable<GlossaryItem[]>;

  constructor(
    private httpService: HttpService,
    private readonly mediaPresignApiService: MediaPresignApiService,
    private readonly documentPreviewService: DocumentPreviewService,
    private readonly downloadService: DownloadService,
    private readonly userActivityService: UserActivityService,
    private readonly dateRangeFilterService: DateRangeFilterService,
    private readonly loadingToastService: LoadingToastService
  ) { }

  public clearCache() {
    this.glossaries = null;
  }

  public getAccessedInfo(): Observable<LibraryAccessedInfo> {
    return this.httpService.get<LibraryAccessedInfo>(`library/accessed-info`);
  }

  public getLibrariesChildren(queryParams: any = {}, parentId: string = 'root'): Observable<LibraryResponseDto> {
    const params: HttpParams = new HttpParams({ fromObject: queryParams });

    return this.httpService.get<LibraryResponseDto>(`libraries/${parentId}/children`, params);
  }

  public getLibraries(queryParams: any = {}): Observable<LibraryResponseDto> {
    const params: HttpParams = new HttpParams({ fromObject: queryParams });

    return this.httpService.get<LibraryResponseDto>(`libraries`, params);
  }

  public getLibraryContentInfo(id: string): Observable<LibraryItem> {
    return this.httpService.get<LibraryItem>(`content-info/${id}`);
  }

  public getGlossaries(): Observable<GlossaryItem[]> {
    if (!this.glossaries) {
      this.glossaries = this.fetchGlosaries()
        .pipe(
          shareReplay({bufferSize: 1, refCount: true}),
          catchError(err => {
            this.glossaries = null;

            throw err;
          })
        );
    }

    return this.glossaries;
  }

  public fetchGlosaries(): Observable<GlossaryItem[]> {
    const params = new HttpParams()
      .set('vocabulary', 'glossary');

    return this.httpService.get<DataResponse<GlossaryItem>>('v2/taxonomies', params).pipe(map(res => res.items));
  }

  public openLibraryFile(item: LibraryItem): void {
    const isZip = ViewerTypeExtensions[FilePreviewType.ZIP_VIEWER].indexOf(item.file?.extension) !== -1;

    if (isZip) {
      this.downloadLibraryFile(item);
    }  else {
      const presignRequest = item.file.provider === ProviderType.AWS ? this.getLibraryItemPresignedUrl(item) : of(null);
      presignRequest.subscribe((presignedUrl: string) => {
        this.markLibraryAsViewed(item.id).subscribe();
        this.documentPreviewService.openPreviewDialog({ file: item.file, entityId: item.id, presignedUrl, isFileDownloadEnabled: item.allowDownload });
        this.userActivityService.logUserPreviewFileActivity(item.id)
      });
    }
  }

  public downloadLibraryFile(item: LibraryItem): void {
    const shouldFetchFile = this.shouldBufferLibraryDownload(item);

    this.getLibraryItemPresignedUrl(item).pipe(
      switchMap(url => this.downloadService.downloadFile(url, item.file.provider, item.file.filename, true, shouldFetchFile)),
      tap(() => this.userActivityService.logUserDownloadFileActivity(item.id))
    ).subscribe();
  }

  public getLibraryItemPresignedUrl(item: LibraryItem): Observable<string> {
    if (item.file.provider !== ProviderType.AWS) {
      return of(item.file.uri);
    }

    this.loadingToastService.start();
    return this.mediaPresignApiService.getPreSignedUrl(
      EntityTypeId.NODE,
      BundleType.LIBRARY,
      FieldName.AWS_FILE,
      item.file,
      item.file.preSignedToken
    ).pipe(
      finalize(() => this.loadingToastService.stop())
    );
  }

  public getLibraryById(id: string): Observable<LibraryItem> {
    const params: HttpParams = new HttpParams().set('id', id)

    return this.httpService.get<LibraryItem>('node', params);
  }

  public pinLibraryById(id: string): Observable<void> {
    return this.httpService.post<void>(`pin/library/${id}`, null);
  }

  public unpinLibraryById(id: string): Observable<void> {
    return this.httpService.post<void>(`unpin/library/${id}`, null);
  }

  public markLibraryAsViewed(id: string): Observable<void> {
    return this.httpService.post<void>(`mark-as-viewed/library/${id}`, null);
  }

  public getQuickFilterList(): any[] {
    const LAST_30_DAYS = this.dateRangeFilterService.mapRangeOptionToDates(DateRangeFilterOption.LAST_30_DAYS, false);
    const LAST_7_DAYS = this.dateRangeFilterService.mapRangeOptionToDates(DateRangeFilterOption.LAST_7_DAYS, false);

    return [
      {
        label: 'LIBRARY.pinned',
        key: LibraryQuickListFilters.PINNED,
        params: {
          pinned: '1'
        },
        emptyMessage: 'LIBRARY.no_items_pinned_in_your_collection'
      },
      {
        label: 'LIBRARY.new',
        key: LibraryQuickListFilters.NEW,
        params: {
          sort: 'created:DESC',
          ...{updatedFrom: LAST_7_DAYS.start, updatedTo: LAST_7_DAYS.end}
        },
        emptyMessage: 'LIBRARY.folder_empty'
      },
      {
        label: 'LIBRARY_PAGE.recently_opened',
        key: LibraryQuickListFilters.RECENTLY_OPENED,
        params: {
          sort: 'viewed:DESC',
          viewed: 1,
          ...{updatedFrom: LAST_30_DAYS.start, updatedTo: LAST_30_DAYS.end}
        },
        emptyMessage: 'LIBRARY.no_recently_opened_items'
      },
      {
        label: 'LIBRARY.recommended',
        key: LibraryQuickListFilters.RECOMMENDED,
        params: {
          recommended: 1
        },
        emptyMessage: 'LIBRARY.no_recommended_files'
      }
    ];
  }

  public getQuickFilter(filter: LibraryQuickListFilters): any {
    const list = this.getQuickFilterList();
    return list.find(item => item.key === filter)
  }

  private shouldBufferLibraryDownload(item: LibraryItem): boolean {
    const extension = item.file?.extension?.toLowerCase();
    const fileSize = Number(item.file?.fileSize);

    if (!extension || !Number.isFinite(fileSize)) {
      return false;
    }

    const bufferedExtensions = [
      ...ViewerTypeExtensions[FilePreviewType.IMAGE_VIEWER],
      ...ViewerTypeExtensions[FilePreviewType.VIDEO_VIEWER],
      ...ViewerTypeExtensions[FilePreviewType.PDF_VIEWER],
    ];

    if (!bufferedExtensions.includes(extension)) {
      return false;
    }

    return fileSize <= LibraryService.MAX_BUFFERED_DOWNLOAD_SIZE_BYTES;
  }
}
